import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Immutable from 'immutable';

import { accountShape, bibShape, branchShape, libraryShape } from '@bibliocommons/bc-prop-types';
import { bindAll } from '@bibliocommons/utils-react';
import { TransactionalSolidButton } from '@bibliocommons/base-buttons';
import ModalContainer from 'app/components/shared/Modal/ModalContainer';
import { FullScreenOverlayContainer } from 'app/components/shared/FullScreenOverlay';
import PhysicalHoldForm from './PhysicalHoldForm';
import { ItemLevelHoldsFormContainer } from './ItemLevelHoldsForm';

export default class PhysicalHoldButton extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
    this.modalKey = `physical-hold-modal-${this.props.bib.get('id')}`;
  }

  componentDidUpdate(prevProps) {
    const { bib, holdActions, holdError } = this.props;

    if (!prevProps.holdError && holdError && holdError.get('classification') === 'HoldIsItemLevel') {
      this.handleModalClose();
      holdActions.openItemLevelHolds({ metadataId: bib.get('id') });
    }
  }

  handleModalClose() {
    this.props.modalActions.closeModal(this.modalKey);
  }

  handleOverlayClose() {
    this.props.holdActions.closeItemLevelHolds({
      metadataId: this.props.bib.get('id')
    });
  }

  handleButtonClick() {
    const { bib, holdActions } = this.props;

    const metadataId = bib.get('id');
    const policy = bib.get('policy', Immutable.Map());
    const itemLevelHoldsPredetermined = policy.getIn(['materialPolicy', 'itemLevelHoldsPredetermined']);
    const itemLevelHolds = itemLevelHoldsPredetermined && policy.get('itemLevelHoldableItems');

    if (itemLevelHolds) {
      return holdActions.openItemLevelHolds({ metadataId });
    }

    return holdActions.startPlaceHold({ metadataId, modalKey: this.modalKey });
  }

  renderItemLevelHoldsOverlay() {
    const { bib, holdsTransactions } = this.props;
    const metadataId = bib.get('id');
    const itemLevelHoldsOpen = holdsTransactions.getIn([metadataId, 'itemLevelHoldsOpen'], false);

    return (
      <FullScreenOverlayContainer
        open={itemLevelHoldsOpen}
        handleClose={this.handleOverlayClose}
        overlayKey="item-level-holds-full-screen-overlay"
        dataKey="item-level-holds-full-screen-overlay"
      >
        {itemLevelHoldsOpen && (
          <ItemLevelHoldsFormContainer metadataId={metadataId} handleClose={this.handleOverlayClose} />
        )}
      </FullScreenOverlayContainer>
    );
  }

  renderPhysicalHoldModal() {
    const {
      bib,
      branches,
      preferredBranches,
      currentAccount,
      currentLibrary,
      holdsTransactions,
      holdActions
    } = this.props;

    return (
      currentAccount && (
        <ModalContainer modalKey={this.modalKey} handleClose={this.handleModalClose}>
          <PhysicalHoldForm
            bib={bib}
            branches={branches}
            preferredBranches={preferredBranches}
            currentAccount={currentAccount}
            currentLibrary={currentLibrary}
            holdsTransactions={holdsTransactions}
            handleClose={this.handleModalClose}
            holdActions={holdActions}
            ref={el => {
              this.holdFormRef = el;
            }}
          />
        </ModalContainer>
      )
    );
  }

  render() {
    const { bib, disabled, holdsTransactions } = this.props;

    const isPlacingSingleClickHold =
      !this.holdFormRef && holdsTransactions.getIn([bib.get('id'), 'isPlacingHold'], false);

    return (
      <div className="cp-physical-hold-button">
        <TransactionalSolidButton
          block
          handleClick={this.handleButtonClick}
          disabled={disabled}
          isLoading={isPlacingSingleClickHold}
        >
          <FormattedMessage id="place_hold" />
        </TransactionalSolidButton>
        {this.renderPhysicalHoldModal()}
        {this.renderItemLevelHoldsOverlay()}
      </div>
    );
  }
}

PhysicalHoldButton.propTypes = {
  bib: bibShape.isRequired,
  disabled: PropTypes.bool,
  currentAccount: accountShape,
  currentLibrary: libraryShape.isRequired,
  branches: ImmutablePropTypes.listOf(branchShape).isRequired,
  preferredBranches: ImmutablePropTypes.listOf(branchShape).isRequired,
  holdsTransactions: ImmutablePropTypes.map.isRequired,
  holdActions: PropTypes.objectOf(PropTypes.func).isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func).isRequired,
  holdError: ImmutablePropTypes.map
};
